import { OrderStatus, OrderItemStatus } from '../enums';

export class MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  image?: string;
  preparationTime: number; // en minutos
  available: boolean;
  /** Cantidad de platos disponibles (null/undefined = sin límite). Al entregar pedidos se disminuye. */
  availableQuantity: number | null = null;
  createdAt: Date;
  updatedAt: Date;

  constructor(
    id: string,
    name: string,
    description: string,
    price: number,
    category: string,
    preparationTime: number = 15
  ) {
    this.id = id;
    this.name = name;
    this.description = description;
    this.price = price;
    this.category = category;
    this.preparationTime = preparationTime;
    this.available = true;
    this.createdAt = new Date();
    this.updatedAt = new Date();
  }
}

export class OrderItem {
  id: string;
  menuItemId: string;
  menuItem?: MenuItem;
  quantity: number;
  status: OrderItemStatus;
  notes?: string;
  price: number;
  subtotal: number;
  createdAt: Date;
  updatedAt: Date;

  constructor(
    id: string,
    menuItemId: string,
    quantity: number,
    price: number
  ) {
    this.id = id;
    this.menuItemId = menuItemId;
    this.quantity = quantity;
    this.price = price;
    this.subtotal = quantity * price;
    this.status = OrderItemStatus.PENDING;
    this.createdAt = new Date();
    this.updatedAt = new Date();
  }

  updateStatus(status: OrderItemStatus): void {
    this.status = status;
    this.updatedAt = new Date();
  }
}

export class Order {
  id: string;
  tableId: string; // Mesa principal (en caso de mesas unidas)
  linkedTableIds: string[]; // Mesas unidas
  status: OrderStatus;
  items: OrderItem[];
  totalAmount: number;
  tax: number;
  discount: number;
  notes?: string;
  qrCode?: string;
  /** Solo en memoria; no se persiste (ej. simulador). */
  isSimulation?: boolean;
  createdAt: Date;
  confirmedAt?: Date;
  completedAt?: Date;
  updatedAt: Date;

  constructor(id: string, tableId: string) {
    this.id = id;
    this.tableId = tableId;
    this.linkedTableIds = [];
    this.status = OrderStatus.DRAFT;
    this.items = [];
    this.totalAmount = 0;
    this.tax = 0;
    this.discount = 0;
    this.createdAt = new Date();
    this.updatedAt = new Date();
  }

  addItem(item: OrderItem): void {
    this.items.push(item);
    this.calculateTotal();
  }

  removeItem(itemId: string): void {
    this.items = this.items.filter(item => item.id !== itemId);
    this.calculateTotal();
  }

  calculateTotal(): void {
    this.totalAmount = this.items.reduce((sum, item) => sum + item.subtotal, 0);
    this.tax = this.totalAmount * 0.16; // 16% por defecto
    this.updatedAt = new Date();
  }

  confirm(): void {
    this.status = OrderStatus.CONFIRMED;
    this.confirmedAt = new Date();
    this.updatedAt = new Date();
  }

  sendToKitchen(): void {
    this.status = OrderStatus.IN_KITCHEN;
    this.updatedAt = new Date();
  }

  markReadyToServe(): void {
    this.status = OrderStatus.READY_TO_SERVE;
    this.updatedAt = new Date();
  }

  complete(): void {
    this.status = OrderStatus.COMPLETED;
    this.completedAt = new Date();
    this.updatedAt = new Date();
  }

  cancel(): void {
    this.status = OrderStatus.CANCELLED;
    this.updatedAt = new Date();
  }

  isCompleted(): boolean {
    return this.status === OrderStatus.COMPLETED;
  }

  isPending(): boolean {
    return this.status === OrderStatus.DRAFT || this.status === OrderStatus.CONFIRMED;
  }
}
